//common
import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate, useRouteLoaderData } from "react-router-dom";
import { Download } from "react-bootstrap-icons";

//deck.gl
import { DeckGL } from "@deck.gl/react";
import { LinearInterpolator, OrthographicView, OrthographicViewState, PickingInfo } from "@deck.gl/core";
import { TileLayer } from "@deck.gl/geo-layers";
import { GeoJsonLayer, TextLayer } from "@deck.gl/layers";
import { DataFilterExtension } from "@deck.gl/extensions";
import type {MjolnirPointerEvent} from 'mjolnir.js';

//satellite
import { AbandonedVehicleStatus, VehicleSituation } from "./api/Network";
import { AbandonedVehicleLoaderData, Match, AreaProps } from "./loaders/abandonedVehicleLoader";
import { aerialImageTileSpec } from "./api/Url";
import { Button } from "./components/Button";
import { showAbandonedVehicleStatus } from "./api/EnumUtils";
import { downloadCsv } from "./Csv";
import { AnnotationFilterBody } from "./components/AnnotationFilter";
import { LocationInfoModal } from "./LocationInfoModal";
import { getImageScale, getStatusColor, lineMidpoint, createObb, createReq } from "./AbandonedVehicleListPageUtil";
import { Api } from "./api/Api";
import { Pixel, pixelToLatLng } from "./common/Geography";
import { createRoundedVehiclePolygon, Obb } from "./common/Geometry";
import { LanczosBitmapLayer } from "./components/LanczosBitmapLayer";
import { DisplayVehicleType, expandAbandonedVehicle } from "./loaders/abandonedVehicleCompressed";
import { AbandonedVehicle } from "./api/Application";
import { AnnotationFilter } from "./components/AnnotationFilter";
import { List, ListImperativeAPI } from "react-window";
import { Row } from "./AbandonedVehicleItem";

const initialValue: AnnotationFilter = {    
    typeFilters:         [null, DisplayVehicleType.Car, DisplayVehicleType.Pickup, DisplayVehicleType.Suv],
    situationFilters:    [
        // VehicleSituation.Street,
        // VehicleSituation.Shoulder,
        // VehicleSituation.Sidewalk,
        // VehicleSituation.Field,
        // VehicleSituation.Alleyway,
        // VehicleSituation.Driveway,
        // VehicleSituation.ParkingLot,
        // VehicleSituation.Junkyard,
        // VehicleSituation.Worksite,
        // VehicleSituation.Industrial,
    ],
    statusFilters:       [AbandonedVehicleStatus.ReviewedVerified],
    confThr:             0.0,
    similarityThr:       0.0,
}

export function AbandonedVehicleMapPage() {
    const nav = useNavigate();
    //drawing controls
    type CreateModeState = {
        rearCenter?:  Pixel;
        frontCenter?: Pixel;
        width:        number;
    };
    const [createMode, setCreateMode] = useState<CreateModeState>();
    //end of drawing controls

    const [hideBoxes,    setHideBoxes]     = useState<boolean>( false );
    const [bTransparent, setBTransparent ] = useState<boolean>( false );
    const [filters,      setFilters]       = useState<AnnotationFilter>( initialValue );
    const { 
        runId, 
        imageA,
        imageB,
        vehicles: initVehicles,
        savedOffset,
        areas,
        roads,
    } = useRouteLoaderData( "run-map" ) as AbandonedVehicleLoaderData;
    const selectedMatch = useRouteLoaderData( "run-map-vehicle" ) as AbandonedVehicle | undefined;
    const [vehicles,      setVehicles]  = useState<Match[]>( initVehicles );
    const [features,      setFeatures]  = useState<any>();
    const [offset,        setOffset]    = useState<[number, number]>( savedOffset ); // dx, dy in pixels
    const [tool,          setTool]      = useState<AbandonedVehicleStatus>();
    const [linePad,       setLinePad]   = useState<number>( 0.25 );
    const [lineWidth,     setLineWidth] = useState<number>( 0.50 );
    const listRef = useRef<ListImperativeAPI>( null );
    
    const aWidth        = Math.ceil( imageA.width  / 512.0 ) * 512;
    const aHeight       = Math.ceil( imageA.height / 512.0 ) * 512;
    const bWidth        = Math.ceil( imageB.width  / 512.0 ) * 512;
    const bHeight       = Math.ceil( imageB.height / 512.0 ) * 512;
    const imageBWidth   = imageB.width;
    const imageBHeight  = imageB.height;
    const lanczosWeight = 3.0;
    const newPolygon = useMemo(() => {
        if( createMode?.rearCenter === undefined || createMode?.frontCenter === undefined ) {
            return [];
        }
        const obb = createObb( createMode.rearCenter, createMode.frontCenter, createMode.width );
        const poly = createRoundedVehiclePolygon( obb );
        return [...poly, poly[0]];
    }, [createMode] );

    // view state for orthographic Deck view
    const [viewState, setViewState] = useState<OrthographicViewState>( () => {
        return {
            target: [ ( selectedMatch !== undefined ? selectedMatch.x : Math.round( imageBWidth  / 2 ) ), 
                      ( selectedMatch !== undefined ? selectedMatch.y : Math.round( imageBHeight / 2 ) ),
                      0 ],
            zoom:   selectedMatch !== undefined ? 3 : 0,
        }
    } );
    const viewStateRef = useRef<OrthographicViewState>( viewState );

    const setSelectedMatch = useCallback( ( match: Match | undefined ) => {
        const path = match !== undefined
            ? `/detect/${runId}/map/${match.abandonedVehicleId}`
            : `/detect/${runId}/map`;
        nav( path, { replace: true } );
    }, [] );

    // click handler for Deck canvas -> mimic MapEvtHandlers click behavior
    async function onDeckClick( info: any ) {
        if( !info?.coordinate ) {
            return;
        }
        const pixel  = info.coordinate as Pixel;
        const [x, y] = pixel;
        if( createMode ) {
            if( !createMode.rearCenter ) {
                setCreateMode( { ...createMode, rearCenter: pixel } );
            } else {
                const rear:  Pixel = createMode.rearCenter;
                const front: Pixel = [x, y] as Pixel;
                const obb:   Obb   = createObb( rear, front, createMode.width );
                const result       = await Api.abandonedVehicleCreate( createReq( runId, obb ) );
                if( result.isOk ) {
                    setVehicles( v => [...v, expandAbandonedVehicle( result.value )] );
                }
                setCreateMode( undefined );
            }
            return;
        }

        // if clicked on nothing (no picked object), open google maps + fetch nearby businesses
        if( info && !info.object ) {
            const coord = info.coordinate; // [x, y, z]
            if( !coord ) 
                return;
            const [ x, y ] = coord;
            const [lat, lng] = pixelToLatLng( x, y, imageB );
            await navigator.clipboard.writeText(`${lat},${lng}`);
            // window.open( `https://www.google.com/maps?q=${lat},${lng}`, "_blank" );
            // fetchNearbyBusinesses( lat, lng ).then( x => setFeatures( x ) );
        }
    }

    const handleDownloadCsv = () => {
        const headers = ["ID", "Lat", "Lon", "Confidence", "Similarity", "Status", "Address"];
        const rows = resultsFiltered.map( v => {
            const [lat, lng] = pixelToLatLng( v.x, v.y, imageB );
            return [
                v.abandonedVehicleId,
                lat,
                lng,
                v.confidence ?? "",
                v.similarity ?? "",
                `"${v.attributes.address ?? ""}"`,
                showAbandonedVehicleStatus( v.status?.status || AbandonedVehicleStatus.Candidate )
            ];
        } );
        downloadCsv( `AbandonedVehiclesInImage${imageB.aerialImageId}.csv`, headers, rows );
    };

    useEffect( () => {
        const handleKeyDown = ( e: KeyboardEvent ) => {
            const esc       = "escape";
            const tab       = "tab";
            const bspace    = "backspace";
            const del       = "delete";
            const shift     = e.shiftKey;
            const alignStep = !shift ? 0.25 : 1.0; //4x for big moves
            const key       = e.key.toLowerCase();
            if( selectedMatch !== undefined ) { //there is a match!
                switch( key ) {
                    case bspace:
                    case del: {                    
                        Api.abandonedVehicleDelete( selectedMatch.abandonedVehicleId )
                            .then( resp => {
                                if( resp.isOk ) {
                                    setVehicles( prev => prev.filter( v => v.abandonedVehicleId !== selectedMatch.abandonedVehicleId ) );
                                }
                            } );
                    }
                }
                return;
            }

            if( selectedMatch !== undefined ) { return; }
            switch( key ) {
                case esc: {
                    setTool(       undefined );
                    setCreateMode( undefined );
                    break;
                }
                case "z": setTool( AbandonedVehicleStatus.Candidate                   ); break;
                case "x": setTool( AbandonedVehicleStatus.ReviewedRejected            ); break; //deprecated
                case "c": setTool( AbandonedVehicleStatus.ReviewedUnsure              ); break;
                case "v": setTool( AbandonedVehicleStatus.ReviewedVerified            ); break;
                case "a": setTool( AbandonedVehicleStatus.ReviewedRejectedIdentity    ); break; //not same car
                case "s": setTool( AbandonedVehicleStatus.ReviewedRejectedMoved       ); break; //same car but not in same position
                case "d": setTool( AbandonedVehicleStatus.ReviewedRejectedNotAVehicle ); break; //both are not a vehicle at all                
                case "f": setTool( AbandonedVehicleStatus.ReviewedRejectedArrived     ); break; //vehicle present in b but not a
                case "g": setTool( AbandonedVehicleStatus.ReviewedRejectedDeparted    ); break; //vehicle present in a but not b

                //create and edit mode
                case "t": setCreateMode( prev => prev ? undefined : { width: 8 } ); break;
                case "w": setCreateMode( prev => prev && { ...prev, width: prev.width + 0.5 } ); break;
                case "q": setCreateMode( prev => prev && { ...prev, width: prev.width - 0.5 } ); break;

                //layer hiding
                case "1": setBTransparent( true  ); break;
                case "2": setBTransparent( false ); break;

                //hide boxes
                case tab: { setHideBoxes( x => !x ); e.preventDefault(); break; }

                //line width
                case "[": setLineWidth( x => x -= 0.25 ); break;
                case "]": setLineWidth( x => x += 0.25 ); break;

                //box padding
                case ";": setLinePad( x => x -= 0.50 ); break;
                case "'": setLinePad( x => x += 0.50 ); break;
                
                //move it over
                case "i": { setOffset( ( [ dx, dy ] ) => [ dx,             dy - alignStep ] ); break; }
                case "k": { setOffset( ( [ dx, dy ] ) => [ dx,             dy + alignStep ] ); break; }
                case "j": { setOffset( ( [ dx, dy ] ) => [ dx - alignStep, dy             ] ); break; }
                case "l": { setOffset( ( [ dx, dy ] ) => [ dx + alignStep, dy             ] ); break; }
            }
        };
        window.addEventListener( "keydown", handleKeyDown );
        return () => window.removeEventListener( "keydown", handleKeyDown );
    }, [selectedMatch] );

    //get appropriate GSD scale so images can be aligned
    const [scaleX,  scaleY ] = getImageScale( imageA, imageB );

    //since we are moving A to match B we negate the stored B -> A offsets
    const [offsetX, offsetY] = [-offset[0], -offset[1]];
    const prevTileLayer = useMemo( () => new TileLayer<ImageBitmap>( {
        id: "aerial-tiles-a",
        pickable: false,
        tileSize: 512,
        refinementStrategy: "best-available",
        minZoom: -3,
        maxZoom:  0,
        extent:   [0, 0, imageA.width, imageA.height],
        data:     aerialImageTileSpec( imageA.aerialImageId ),
        modelMatrix: [
            scaleX,  0,       0, 0,
            0,       scaleY,  0, 0,
            0,       0,       1, 0,
            offsetX, offsetY, 0, 1
        ],
        updateTriggers: {
            renderSubLayers: [lanczosWeight]
        },
        renderSubLayers: props => {
            const [[left, bottom], [right, top]] = props.tile.boundingBox;
            const { data, ...otherProps } = props;          
            return new LanczosBitmapLayer( otherProps, {
                weight: lanczosWeight,
                image: data,
                bounds: [ left, top, right, bottom ],
                textureParameters: {
                    minFilter: "nearest",
                    magFilter: "nearest"
                }
            } );
        }
    } ), [ imageA, aWidth, aHeight, offset, lanczosWeight ] );

    const tileLayer = useMemo( () => new TileLayer<ImageBitmap>( {
        opacity:            bTransparent ? 0.0 : 1.0,
        id:                 "aerial-tiles-b",
        pickable:           false,
        tileSize:           512,
        refinementStrategy: "best-available",
        minZoom:            -3,
        maxZoom:            0,
        extent:             [0, 0, bWidth, bHeight],
        data:               aerialImageTileSpec( imageB.aerialImageId ),
        updateTriggers: {
            renderSubLayers: [lanczosWeight]
        },
        renderSubLayers: props => {
            const [ [ left, bottom ], [ right, top ] ] = props.tile.boundingBox;
            const { data, ...otherProps } = props;
            return new LanczosBitmapLayer( otherProps, {
                weight: lanczosWeight,
                image: data,
                transitions: {
                    opacity: { duration: 150 }
                },
                bounds: [ left, top, right, bottom ],
                textureParameters: {
                    minFilter: "nearest",
                    magFilter: "nearest"
                }
            } );
        }
    } ), [ bTransparent, imageB, bWidth, bHeight, lanczosWeight ] );    
    
    const preppedVehicles = useMemo( () => {
        return vehicles.map( v => ({
            ...v,
            attributes: { ...v.attributes, area: v.attributes.area ?? -1 }
        }) );
    }, [vehicles] );

    const polygons = useMemo( () => {
        const { typeFilters, situationFilters, statusFilters } = filters;
        const res = preppedVehicles
            .filter( v => typeFilters.length      > 0 ? typeFilters.includes( v.displayVehicleType )        : true )
            .filter( v => situationFilters.length > 0 ? situationFilters.includes( v.attributes.situation ) : true )
            .filter( v => statusFilters.includes( v.status.status ) )
            .map( ( m ) => {
                const coords = createRoundedVehiclePolygon( m, linePad );
                return {
                    type: "Feature" as const,
                    geometry: {
                        type: "Polygon" as const,
                        coordinates: [coords],
                    },
                    properties: { ...m },
                };
            } );
        return {
            type: "FeatureCollection" as const,
            features: res,
        };
    }, [preppedVehicles, filters.statusFilters, filters.typeFilters, filters.situationFilters, linePad] );

    const geoJsonLayer = useMemo( () => {
        return new GeoJsonLayer( {
            id: "vehicles",
            data: polygons,
            pickable: true,
            autoHighlight: true,
            highlightColor: tool !== undefined ? [...getStatusColor( tool ).slice( 0, 3 ), 100] : [0, 256, 0, 100],
            filled: true,
            stroked: true,
            lineJointRounded: true,
            getFillColor: [0, 0, 0, 0],
            getLineColor: ( f: any ) => getStatusColor( f.properties.status.status as AbandonedVehicleStatus ),
            getLineWidth: lineWidth,
            lineWidthMinPixels: 2,
            coordinateSystem: "cartesian",
            extensions: [new DataFilterExtension( { filterSize: 2 } )],
            visible: !hideBoxes,
            // order matters: one slot per threshold
            getFilterValue: ( f: any ) => [
                f.properties.confidence ?? 0, // [0, 1]
                f.properties.similarity ?? 0, // [0, 1]
            ],
            filterRange: [
                [filters.confThr,       1.0], // similarity above threshold
                [filters.similarityThr, 1.0], // similarity above threshold
            ],
            updateTriggers: {
                getFilterValue: [
                    filters.confThr,
                    filters.similarityThr,
                ],
            },
            onClick: ( info, event ) => {
                if( info && info.object && info.object.properties ) {
                    const m = info.object.properties as Match;
                    handleMatchClick( m );
                }
            },
        } );
    }, [vehicles, filters, tool, hideBoxes, lineWidth, linePad] );    

    const resultsAll = useMemo( () => {
        return vehicles.toSorted( ( a, b ) => a.abandonedVehicleId - b.abandonedVehicleId );
    }, [vehicles] );

    const resultsFiltered = useMemo( () => {
        const { typeFilters, situationFilters, statusFilters, confThr, similarityThr } = filters;
        return resultsAll
            .filter( v => typeFilters.length      > 0 ? typeFilters.includes( v.displayVehicleType    ) : true )
            .filter( v => situationFilters.length > 0 ? situationFilters.includes( v.attributes.situation ) : true )
            .filter( v => statusFilters.includes( v.status.status ) )
            .filter( v => !confThr       || ( v.confidence > confThr ) )
            .filter( v => !similarityThr || ( v.similarity > similarityThr ) )
    }, [resultsAll, filters] );

    useEffect( () => {
        if( selectedMatch === undefined ) {
            return;
        }
        const id  = selectedMatch.abandonedVehicleId;
        const ind = resultsFiltered.findIndex( v => v.abandonedVehicleId === id );
        if( ind === -1 ) {
            setSelectedMatch( undefined ); 
            return;
        }
    }, [selectedMatch, resultsFiltered] );

    function getTargetViewState( viewState: OrthographicViewState, match: Match ): OrthographicViewState {
        return {
            ...viewState,
            target:                 [match.x, match.y, 0],
            transitionInterpolator: new LinearInterpolator( { transitionProps: ["target", "zoomX", "zoomY"] }),
            transitionDuration:     500,
            transitionEasing:       t => t * ( 2 - t ),
        };
    }

    const handleSidebarMatchClick = useCallback( ( match: Match ) => {
        setSelectedMatch( match );
        const from = viewStateRef.current.target as Pixel;
        const to   = [match.x, match.y, 0];
        const dist = Math.hypot( to[0] - from[0], to[1] - from[1] );
        console.log( "from:", from, "to:", to, "dist:", dist );
        setViewState( getTargetViewState( viewStateRef.current, match) );
    }, [setSelectedMatch, setViewState] );

    const handleMatchClick = useCallback( async ( match: Match ) => {
        if( tool !== undefined ) {
            const result = await Api.abandonedVehicleUpdate( match.abandonedVehicleId, {
                note:   "",
                status: tool
            } );
            if( !result.isOk ) {
                throw new Error( "Error saving vehicle" );
            }
            const vehicle = expandAbandonedVehicle( result.value );
            const updated = [...vehicles.filter( iv => vehicle.abandonedVehicleId != iv.abandonedVehicleId ), vehicle];
            setVehicles( updated );            
            return;
        }
        //else
        handleSidebarMatchClick( match );
    }, [vehicles, resultsAll, tool] );

    const previewLayer = useMemo( () => new GeoJsonLayer( {
        id: "create-preview",
        data: {
            type:           "FeatureCollection" as const, features: newPolygon.length ? [{
                type:       "Feature" as const,
                geometry:   { type: "Polygon" as const, coordinates: [newPolygon] },
                properties: {},
            }] : []
        },
        getFillColor:       [255, 255, 0,  40],
        getLineColor:       [255, 255, 0, 200],
        getLineWidth:       lineWidth,
        lineWidthMinPixels: 2,
        coordinateSystem:   "cartesian",
    } ), [newPolygon] );

    function onDeckHover( info: PickingInfo, event: MjolnirPointerEvent ): void {
        if( createMode?.rearCenter && info?.coordinate ) {
            setCreateMode( m => m && ( { ...m, frontCenter: info.coordinate as Pixel } ) );
        }
    }

    return <div className="h-100">
        {/* <AbandonedVehicleModal
            imageA={imageA}
            imageB={imageB}
            show={selectedMatch}
            setShow={ () => setSelectedMatch( undefined ) }
            save={ v => {
                const updated = [...vehicles.filter( iv => v.abandonedVehicleId != iv.abandonedVehicleId ), v];
                setVehicles( updated );
            }} /> */}

        <LocationInfoModal
            features={features}
            onClose={() => setFeatures( undefined )} />

        <div id="capture-list-page">
            <div id="capture-list">
                <div className="position-relative bg-body-tertiary border border-end-0 d-flex flex-row w-100 justify-content-center align-items-center gap-2 p-2">
                    <div className="flex-grow-1 ps-2 fs-5">
                        {resultsFiltered.length} Detections
                        {/* &nbsp;{zoom.toFixed(2)} {opac} {roadOpac} */}
                    </div>
                    <Button className="bg-body fs-6 px-2 py-1 rounded" onClick={handleDownloadCsv}>
                        <Download />
                    </Button>
                </div>
                <div className="h-100 p-2" style={{ minHeight: 0, display: "grid", gridTemplateRows: "min-content 1fr"}}>
                    <AnnotationFilterBody filters={filters} setFilters={setFilters} />
                    <div className="h-100 y-scroll-auto" style={{ minHeight: 0 }}>
                        <List
                            listRef={listRef}
                            rowComponent={Row}
                            rowCount={resultsFiltered.length}
                            rowHeight={120}
                            rowProps={{
                                selectedId: selectedMatch?.abandonedVehicleId,
                                vehicles: resultsFiltered,
                                onClick: handleSidebarMatchClick
                            }}
                            overscanCount={6} />
                    </div>
                </div>
            </div>
            <div className="image-viewer h-100 overflow-hidden position-relative" style={{ backgroundColor: "black" }}>
                {tool && <div className="position-relative p-2 d-flex flex-column gap-2" style={{ left: "0.75ex", top: "0.75ex", zIndex: 5, width: "max-content" }}>
                    <div className="d-flex align-items-center justify-content-center rounded border border-black small p-2 bg-body text-body">
                        <div>{showAbandonedVehicleStatus( tool )}</div>
                    </div>
                </div>}
                <DeckGL
                    views={ new OrthographicView( {
                        controller: {
                            maxBounds:       [[0, 0], [imageB.width, imageB.height]],
                            doubleClickZoom: false //reduces click delay according to github issue discussion https://github.com/visgl/deck.gl/issues/3783
                        }
                    } )}
                    layers={[
                        prevTileLayer,
                        tileLayer,
                        previewLayer,
                        geoJsonLayer
                    ]}
                    initialViewState={ viewState }
                    onDrag={ () => {
                        setSelectedMatch( undefined );                        
                    }}
                    onClick={onDeckClick}
                    onHover={onDeckHover}
                    style={{ backgroundColor: "black" }}
                    getCursor={( { isDragging, isHovering } ) => {
                        if( isDragging ) { return "grabbing";  }
                        if( isHovering ) { return "pointer";   }
                        if( createMode ) { return "crosshair"; }
                        return "grab";
                    }}
                />
            </div>
        </div>
    </div>;
}

